import React, { useState, useEffect } from 'react';
import { fetchWrapper } from '../utils/fetchWrapper';
import { toast } from 'react-hot-toast';
import {
    MdShield,
    MdRefresh,
    MdBlock,
    MdHistory,
    MdVerifiedUser,
    MdInfo,
    MdList,
    MdLock,
    MdAccessTime,
    MdLockOpen,
    MdSwapHoriz,
    MdDelete
} from 'react-icons/md';
import ConfirmModal from './ui/ConfirmModal';
import { formatDate } from '../utils/units';

function BlockedIPs() {
    const [blockedIPs, setBlockedIPs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [serverTime, setServerTime] = useState(new Date());

    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        show: false,
        title: '',
        message: '',
        detail: '',
        confirmText: 'Confirm',
        variant: 'danger',
        loading: false,
        confirmPhrase: '',
        onConfirm: null,
    });

    const loadBlockedIPs = async () => {
        setLoading(true);
        try {
            const res = await fetchWrapper.get('/leads/blocked-ips');
            setBlockedIPs(res.data.data || []);
            setServerTime(new Date(res.data.serverTime || new Date()));
        } catch (err) {
            console.error('Failed to fetch blocked IPs', err);
        }
        setLoading(false);
    };

    const formatRemainingTime = (remainingMs) => {
        if (!remainingMs || remainingMs <= 0) return null;
        const totalSeconds = Math.floor(remainingMs / 1000);
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);

        if (days > 0) return `${days}d ${hours}h left`;
        if (hours > 0) return `${hours}h ${minutes}m left`;
        return `${minutes}m left`;
    };

    useEffect(() => {
        loadBlockedIPs();
    }, []);

    const unblockIP = (ip) => {
        setConfirmModal({
            show: true,
            title: 'Unblock IP Address',
            message: `Are you sure you want to unblock ${ip}?`,
            detail: 'This IP will regain access to the server immediately.',
            confirmText: 'Unblock IP',
            variant: 'warning',
            loading: false,
            confirmPhrase: 'confirm',
            onConfirm: () => executeUnblock(ip),
        });
    };

    const executeUnblock = async (ip) => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
            await fetchWrapper.delete(`/leads/unblock-ip/${ip}`);
            toast.success(`IP ${ip} has been unblocked`);
            setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
            loadBlockedIPs();
        } catch (err) {
            toast.error('Failed to unblock IP: ' + (err.response?.data?.message || err.message));
            setConfirmModal(prev => ({ ...prev, loading: false }));
        }
    };

    const togglePermanent = (ip, currentlyPermanent) => {
        const newState = currentlyPermanent ? 'Temporary' : 'Permanent';
        setConfirmModal({
            show: true,
            title: `Switch to ${newState}`,
            message: `Change block type for ${ip} to ${newState.toLowerCase()}?`,
            detail: currentlyPermanent
                ? 'The IP will auto-expire after 7 days.'
                : 'The IP will be blocked indefinitely until manually removed.',
            confirmText: `Make ${newState}`,
            variant: 'info',
            loading: false,
            onConfirm: () => executeToggle(ip),
        });
    };

    const executeToggle = async (ip) => {
        setConfirmModal(prev => ({ ...prev, loading: true }));
        try {
            await fetchWrapper.patch(`/leads/block-ip/${ip}/toggle-permanent`);
            toast.success('Block type updated');
            setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
            loadBlockedIPs();
        } catch (err) {
            toast.error('Failed to toggle status: ' + (err.response?.data?.message || err.message));
            setConfirmModal(prev => ({ ...prev, loading: false }));
        }
    };

    const closeConfirmModal = () => {
        setConfirmModal(prev => ({ ...prev, show: false, loading: false }));
    };

    const getExpiryStats = () => {
        const hasTemp = blockedIPs.some(ip => !ip.permanent);
        const hasPerm = blockedIPs.some(ip => ip.permanent);

        if (hasTemp && hasPerm) return "Mixed (Temp + Perm)";
        if (hasTemp) return "7 Days Auto-expiry";
        if (hasPerm) return "No Auto-expiry";
        return "N/A";
    };

    return (
        <div className="security-container">
            <div className="security-header">
                <div>
                    <h3 className="security-title">
                        <MdShield className="text-danger me-2" /> Blocked IPs
                    </h3>
                    <p className="text-muted mb-0">Manage blocked IP addresses and security rules</p>
                </div>
                <button onClick={loadBlockedIPs} className="btn btn-outline-primary btn-sm">
                    <MdRefresh className="me-1" /> Refresh List
                </button>
            </div>

            {/* Stats Overview */}
            <div className="security-stats-grid">
                <div className="security-stat-card">
                    <div className="stat-icon-wrapper stat-icon-danger">
                        <MdBlock />
                    </div>
                    <div className="stat-content">
                        <h3>{blockedIPs.length}</h3>
                        <p>Total Blocked IPs</p>
                    </div>
                </div>
                <div className="security-stat-card">
                    <div className="stat-icon-wrapper stat-icon-warning">
                        <MdHistory />
                    </div>
                    <div className="stat-content">
                        <h3>{getExpiryStats()}</h3>
                        <p>Policy Status</p>
                    </div>
                </div>
                <div className="security-stat-card">
                    <div className="stat-icon-wrapper stat-icon-info">
                        <MdVerifiedUser />
                    </div>
                    <div className="stat-content">
                        <h3>Active</h3>
                        <p>Firewall Status</p>
                    </div>
                </div>
            </div>

            {/* Info Alert */}
            <div className="alert alert-light border mb-4 d-flex align-items-center shadow-sm">
                <MdInfo className="me-2 text-primary" size={20} />
                <span className="text-muted small">
                    Blocked IPs are automatically removed after 7 days unless marked as permanent.
                </span>
            </div>

            {/* Blocked IPs Table */}
            <div className="security-card">
                <div className="security-card-header">
                    <h6 className="security-card-title">
                        <MdList className="me-2" /> Block List
                    </h6>
                    {loading && <div className="spinner-border spinner-border-sm text-primary" role="status"></div>}
                </div>

                {blockedIPs.length === 0 ? (
                    <div className="text-center py-5">
                        <div className="mb-3">
                            <MdVerifiedUser className="text-success" style={{ fontSize: '3rem' }} />
                        </div>
                        <h5 className="text-muted">No Blocked IPs</h5>
                        <p className="text-muted small">Your server is clean - no IPs are currently blocked</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="security-table">
                            <thead>
                                <tr>
                                    <th>IP Address</th>
                                    <th>Reason</th>
                                    <th>Blocked At</th>
                                    <th>Type</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {blockedIPs.map((blocked) => (
                                    <tr key={blocked._id}>
                                        <td data-label="IP Address">
                                            <code className="text-danger fw-bold">{blocked.ip}</code>
                                        </td>
                                        <td data-label="Reason">
                                            <span className="text-muted">{blocked.reason || 'No reason provided'}</span>
                                        </td>
                                        <td data-label="Blocked At">
                                            <div className="small text-muted">
                                                {formatDate(blocked.createdAt, "dd-mm-yyyy-time")}
                                            </div>
                                        </td>
                                        <td data-label="Type">
                                            {blocked.permanent ? (
                                                <span className="badge-security badge-blocked">
                                                    <MdLock className="me-1" /> Permanent
                                                </span>
                                            ) : (
                                                <span className="badge-security badge-temporary">
                                                    <MdAccessTime className="me-1" /> Temporary
                                                    <div className="remaining-time-hint" style={{ fontSize: '0.7rem', opacity: 0.8 }}>
                                                        {formatRemainingTime(blocked.remainingTime)}
                                                    </div>
                                                </span>
                                            )}
                                        </td>
                                        <td data-label="Actions">
                                            <div className="d-flex gap-2">
                                                <button
                                                    className="btn-action-square btn-action-view"
                                                    onClick={() => togglePermanent(blocked.ip, blocked.permanent)}
                                                    title={blocked.permanent ? "Make Temporary" : "Make Permanent"}
                                                >
                                                    <MdSwapHoriz />
                                                </button>
                                                <button
                                                    className="btn-action-square btn-action-danger"
                                                    onClick={() => unblockIP(blocked.ip)}
                                                    title="Unblock"
                                                >
                                                    <MdDelete />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Confirmation Modal */}
            <ConfirmModal
                show={confirmModal.show}
                title={confirmModal.title}
                message={confirmModal.message}
                detail={confirmModal.detail}
                confirmText={confirmModal.confirmText}
                variant={confirmModal.variant}
                loading={confirmModal.loading}
                confirmPhrase={confirmModal.confirmPhrase}
                onConfirm={confirmModal.onConfirm}
                onCancel={closeConfirmModal}
            />
        </div>
    );
}

export default BlockedIPs;
